import { useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@krea/shared/design-system/Button";
import { AppModal } from "@krea/shared/design-system/modal";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { toast } from "@krea/shared/design-system/utils/toast";

import { LenderUserRoleEnum } from "@/features/common/enums";
import { queryKeyMap } from "@/features/query-client";
import { useDeleteUserMutation } from "@/features/users/hooks/use-delete-user.mutation";
import { useGetLenderCompanyUsers } from "@/features/users/hooks/use-get-lender-company-users";
import type { ILenderUser } from "@/features/users/types";

import { useGetCurrentLenderCompanyQuery } from "../../hooks/use-get-lender-company.query";
import { EditTeamMemberModal } from "./edit-team-member-modal";

export const TeamMemberActions = ({ user }: { user: ILenderUser }) => {
  const [isEditTeamMemberModalOpen, setIsEditTeamMemberModalOpen] =
    useState(false);

  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();

  const { data: usersData } = useGetLenderCompanyUsers(
    Number(lenderCompany?.id),
  );

  const totalAdmins = useMemo(
    () =>
      usersData?.filter(
        (user) => user.settings?.role === LenderUserRoleEnum.ADMIN,
      ).length ?? 0,
    [usersData],
  );
  const [isDeleteTeamMemberModalOpen, setIsDeleteTeamMemberModalOpen] =
    useState(false);
  const { mutate: deleteUser } = useDeleteUserMutation();

  const queryClient = useQueryClient();

  const handleDeleteUser = () => {
    if (!lenderCompany) return;
    deleteUser(
      { lenderCompanyId: lenderCompany.id, userId: user.id },
      {
        onSuccess: () => {
          setIsDeleteTeamMemberModalOpen(false);
          toast.success(
            `User ${user.firstName} ${user.lastName} removed successfully`,
          );
          queryClient.invalidateQueries({
            queryKey: queryKeyMap.getLenderCompanyUsers(lenderCompany.id),
          });
        },
      },
    );
  };

  return (
    <div className="flex justify-end gap-1">
      {isEditTeamMemberModalOpen && (
        <EditTeamMemberModal
          isOpen={isEditTeamMemberModalOpen}
          setIsOpen={setIsEditTeamMemberModalOpen}
          user={user}
        />
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        aria-label="Row actions"
        onClick={() => setIsEditTeamMemberModalOpen(true)}
      >
        <Pencil className="h-4 w-4 text-primary" strokeWidth={3} />
      </Button>
      {isDeleteTeamMemberModalOpen && (
        <AppModal
          open={isDeleteTeamMemberModalOpen}
          setOpen={setIsDeleteTeamMemberModalOpen}
          lockOutsideInteraction={false}
          title="Remove Team Member"
          onClose={() => setIsDeleteTeamMemberModalOpen(false)}
          footer={
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => setIsDeleteTeamMemberModalOpen(false)}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={handleDeleteUser}>
                Remove
              </Button>
            </div>
          }
        >
          <div>Are you sure you want to remove user with ssn {user.ssn}?</div>
        </AppModal>
      )}
      <ResponsiveTooltip
        hidden={
          totalAdmins === 1 && user.settings?.role === LenderUserRoleEnum.ADMIN
        }
        trigger={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={
              totalAdmins === 1 &&
              user.settings?.role === LenderUserRoleEnum.ADMIN
            }
            className="group h-8 w-8 disabled:bg-transparent"
            aria-label="Row actions"
            onClick={() => setIsDeleteTeamMemberModalOpen(true)}
          >
            <Trash
              className="h-4 w-4 text-red-500 group-disabled:text-gray-500"
              strokeWidth={3}
            />
          </Button>
        }
        interactive
      >
        You cannot remove the last admin from the team. Make sure to have at
        least one admin in the team.
      </ResponsiveTooltip>
    </div>
  );
};
